'use client';

import Link from 'next/link';
import { ArrowLeft, Grid3X3, Heart } from 'lucide-react';
import { useBasePath } from '../navigation/base-path';
import { S3Image } from './s3-image';
import { VideoThumbnail } from './video-thumbnail';

export interface PublicProfileScreenData {
  id: string;
  username: string | null;
  firstName: string | null;
  stats: {
    totalPublished: number;
    totalReferrals: number;
  };
  gallery: {
    id: string;
    resultUrl: string | null;
    thumbnailUrl: string | null;
    resultType: 'IMAGE' | 'VIDEO' | 'AUDIO';
    prompt: string | null;
    likesCount: number;
    createdAt: string;
  }[];
  createdAt: string;
}

interface PublicProfileScreenProps {
  profile?: PublicProfileScreenData | null;
  isLoading?: boolean;
  hasError?: boolean;
  onBack: () => void;
}

export function PublicProfileScreen({
  profile,
  isLoading = false,
  hasError = false,
  onBack,
}: PublicProfileScreenProps) {
  const basePath = useBasePath();

  if (isLoading) {
    return (
      <div className="pb-20">
        <div className="space-y-4 p-4">
          <div className="flex flex-col items-center">
            <div className="h-20 w-20 animate-pulse rounded-full bg-secondary" />
            <div className="mt-3 h-5 w-32 animate-pulse rounded bg-secondary" />
            <div className="mt-2 h-4 w-24 animate-pulse rounded bg-secondary" />
          </div>
          <div className="h-16 animate-pulse rounded-xl bg-secondary" />
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} className="aspect-square animate-pulse rounded-lg bg-secondary" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (hasError || !profile) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4">
        <p className="text-center text-muted-foreground">
          Пользователь не найден
        </p>
        <button
          onClick={onBack}
          className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Назад
        </button>
      </div>
    );
  }

  return (
    <div className="pb-20">
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-background/80 p-4 backdrop-blur-lg">
        <button
          onClick={onBack}
          className="invisible flex h-10 w-10 items-center justify-center rounded-full bg-secondary"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold">Профиль</h1>
        </div>
      </div>

      <div className="flex flex-col items-center p-6 pb-4">
        <div className="relative">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60 text-3xl font-bold text-white">
            {profile.firstName?.[0] || '?'}
          </div>
        </div>

        <h1 className="mt-3 text-xl font-bold">{profile.firstName || 'Пользователь'}</h1>
        {profile.username && (
          <p className="text-sm text-muted-foreground">@{profile.username}</p>
        )}

        <div className="mt-4 flex items-center gap-8">
          <div className="text-center">
            <p className="text-xl font-bold">{profile.stats.totalPublished}</p>
            <p className="text-xs text-muted-foreground">публикаций</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold">{profile.stats.totalReferrals}</p>
            <p className="text-xs text-muted-foreground">друзей</p>
          </div>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="flex items-center justify-center gap-2 py-3">
          <Grid3X3 className="h-4 w-4" />
          <span className="text-sm font-medium">Публикации</span>
        </div>

        {profile.gallery.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
            <Grid3X3 className="h-12 w-12 text-muted-foreground/50" />
            <p className="mt-3 text-muted-foreground">Нет публикаций</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-0.5 px-0.5">
            {profile.gallery.map((post) => (
              <Link
                key={post.id}
                href={`${basePath}/history/${post.id}`}
                className="relative aspect-square"
              >
                {post.resultUrl && (
                  post.resultType === 'VIDEO' ? (
                    <VideoThumbnail
                      src={post.resultUrl}
                      poster={post.thumbnailUrl}
                      alt={post.prompt || 'Video'}
                      iconSize="md"
                    />
                  ) : (
                    <S3Image
                      src={post.thumbnailUrl || post.resultUrl}
                      alt={post.prompt || 'Generated'}
                      fill
                      className="object-cover"
                    />
                  )
                )}
                <div className="absolute bottom-1 right-1 flex items-center gap-1 rounded bg-black/50 px-1.5 py-0.5">
                  <Heart className="h-3 w-3 text-white" />
                  <span className="text-xs text-white">{post.likesCount}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
